import { useState, useEffect, useRef } from 'react';

export type UnifiedSearchableSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  searchText?: string;
};

export type UnifiedSearchableSelectProps = {
  id: string;
  options: UnifiedSearchableSelectOption[];
  multiple?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  className?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  onBlur?: () => void;
  ariaRequired?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  removeItemAriaLabel?: (optionLabel: string) => string;
} & (
  | {
      multiple: true;
      value: string[];
      defaultValue?: string[];
      onChange?: (value: string[]) => void;
    }
  | {
      multiple?: false;
      value?: string;
      defaultValue?: string;
      onChange?: (value: string) => void;
    }
);

const UnifiedSearchableSelect = (props: UnifiedSearchableSelectProps) => {
  const {
    id,
    options,
    multiple = false,
    searchable = false,
    disabled = false,
    readOnly = false,
    placeholder = '',
    searchPlaceholder = 'Rechercher...',
    noResultsText = 'Aucune option trouvée',
    className = '',
    autoFocus,
    tabIndex,
    removeItemAriaLabel = (label: string) => `Remove ${label}`,
    onBlur,
    ariaRequired,
    ariaInvalid,
    ariaDescribedBy,
  } = props;

  const valueProp = props.value;
  const defaultValueProp = props.defaultValue;
  const onChangeProp = props.onChange;

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    if (multiple) {
      const v = valueProp ?? defaultValueProp;
      if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
        return v;
      }
      return [];
    }
    const single =
      valueProp !== undefined ? valueProp : defaultValueProp !== undefined ? defaultValueProp : '';
    return single ? [String(single)] : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<UnifiedSearchableSelectOption[]>(options);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchable || !searchQuery) {
      setFilteredOptions(options);
      return;
    }

    const queryLower = searchQuery.toLowerCase();
    const filtered = options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(queryLower);
      const searchTextMatch = option.searchText?.toLowerCase().includes(queryLower);
      return labelMatch || searchTextMatch;
    });

    setFilteredOptions(filtered);
  }, [searchQuery, options, searchable]);

  useEffect(() => {
    if (multiple) {
      const v = valueProp;
      if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
        setSelectedValues(v);
      }
      return;
    }
    const single = valueProp !== undefined ? valueProp : '';
    setSelectedValues(single ? [String(single)] : []);
  }, [valueProp, multiple]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }

      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceNeeded = 200;

        if (spaceBelow < spaceNeeded && rect.top > spaceNeeded) {
          setDropdownPosition('above');
        } else {
          setDropdownPosition('below');
        }
      }
    }
  }, [isOpen, searchable]);

  const emitChange = (next: string[]) => {
    if (multiple) {
      (onChangeProp as ((value: string[]) => void) | undefined)?.(next);
    } else {
      (onChangeProp as ((value: string) => void) | undefined)?.(next[0] ?? '');
    }
  };

  const handleSelectOption = (option: UnifiedSearchableSelectOption) => {
    if (option.disabled) {
      return;
    }

    let newValues: string[];

    if (multiple) {
      if (selectedValues.includes(option.value)) {
        newValues = selectedValues.filter((val) => val !== option.value);
      } else {
        newValues = [...selectedValues, option.value];
      }

      setSelectedValues(newValues);
      setSearchQuery('');
      emitChange(newValues);
    } else {
      newValues = [option.value];
      setSelectedValues(newValues);
      setIsOpen(false);
      setSearchQuery('');
      emitChange(newValues);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRemoveItem = (e: React.MouseEvent, valueToRemove: string) => {
    e.stopPropagation();

    const newValues = selectedValues.filter((val) => val !== valueToRemove);
    setSelectedValues(newValues);
    emitChange(newValues);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          setIsOpen(false);
          setSearchQuery('');
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (!multiple) {
          const currentIndex = filteredOptions.findIndex(
            (opt) => opt.value === (selectedValues[0] || ''),
          );
          const nextIndex = Math.min(currentIndex + 1, filteredOptions.length - 1);
          if (nextIndex >= 0 && nextIndex < filteredOptions.length) {
            const nextOption = filteredOptions[nextIndex];
            if (!nextOption.disabled) {
              setSelectedValues([nextOption.value]);
              selectedOptionRef.current?.scrollIntoView({ block: 'nearest' });
            }
          }
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (!multiple) {
          const currentIndex = filteredOptions.findIndex(
            (opt) => opt.value === (selectedValues[0] || ''),
          );
          const prevIndex = Math.max(currentIndex - 1, 0);
          if (prevIndex >= 0) {
            const prevOption = filteredOptions[prevIndex];
            if (!prevOption.disabled) {
              setSelectedValues([prevOption.value]);
              selectedOptionRef.current?.scrollIntoView({ block: 'nearest' });
            }
          }
        }
        break;
      default:
        break;
    }
  };

  const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value));

  const singleDisplayValue =
    selectedOptions.length > 0 ? selectedOptions[0].label : placeholder;

  const renderMultipleValues = () => {
    if (selectedOptions.length === 0) {
      return <span className="tpct-select-placeholder">{placeholder}</span>;
    }

    return selectedOptions.map((option, index) => (
      <span key={`${option.value}-${index}`} className="tpct-select-value-item">
        <button
          type="button"
          className="tpct-select-value-item-remove"
          onClick={(e) => handleRemoveItem(e, option.value)}
          aria-label={removeItemAriaLabel(option.label)}
        >
          <span aria-hidden="true">×</span>
        </button>
        {option.label}
      </span>
    ));
  };

  const listboxId = `listbox-${id}`;

  return (
    <div
      ref={dropdownRef}
      className={`tpct-unified-select ${multiple ? 'tpct-unified-select--multiselect' : ''} ${isOpen ? 'open' : ''} ${isOpen ? 'dropdown-open' : ''} ${disabled ? 'disabled' : ''} ${readOnly ? 'readonly' : ''} ${className}`.trim()}
    >
      <div
        className="tpct-select-trigger"
        onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
        onBlur={() => onBlur?.()}
        onKeyDown={handleKeyDown}
        tabIndex={disabled || readOnly ? -1 : tabIndex ?? 0}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-required={ariaRequired}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled}
        aria-readonly={readOnly}
        autoFocus={autoFocus}
      >
        <div className="tpct-select-value">
          {multiple ? (
            <span className="tpct-select-placeholder">{placeholder}</span>
          ) : selectedValues.length > 0 ? (
            <span className="tpct-select-value-item">{singleDisplayValue}</span>
          ) : (
            <span className="tpct-select-placeholder">{placeholder}</span>
          )}
        </div>

        <span className="tpct-select-arrow" aria-hidden="true">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {multiple && selectedOptions.length > 0 ? (
        <div className="tpct-select-selected-items-persistent">{renderMultipleValues()}</div>
      ) : null}

      {isOpen && !disabled && !readOnly ? (
        <div
          className={`tpct-select-dropdown ${dropdownPosition === 'above' ? 'dropdown-above' : ''}`}
          role="listbox"
          id={listboxId}
        >
          {searchable ? (
            <div className="tpct-select-search">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="tpct-select-search-input"
                aria-autocomplete="list"
                aria-controls={listboxId}
              />
            </div>
          ) : null}

          <div className="tpct-select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  ref={
                    multiple ? null : selectedValues[0] === option.value ? selectedOptionRef : null
                  }
                  className={`tpct-select-option ${selectedValues.includes(option.value) ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  role="option"
                  aria-selected={selectedValues.includes(option.value)}
                  aria-disabled={option.disabled}
                >
                  {multiple ? (
                    <span
                      className={`tpct-select-option-checkbox ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                    />
                  ) : null}
                  {option.label}
                </div>
              ))
            ) : (
              <div className="tpct-select-no-results">{noResultsText}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UnifiedSearchableSelect;
